<template>
  <div>
    <!-- Trigger button -->
    <button type="button" class="button button--secondary" @click="openModal">
      Check in
    </button>

    <!-- Modal with full-width camera and custom overlay -->
    <BaseModal
      ref="modal"
      @closed="cleanup"
      :notifications="notifications"
      :title="`Check-in for ${eventEdition?.name || ''}`"
      centered
    >
      <Transition name="fade-scale" mode="out-in">
        <div v-if="checkinSuccess" style="margin: var(--sizing-4)">
          <AnimatedCheckmark :size="24" />

          <p class="checkin-success-instructions">
            Proceed to <strong>table {{ meeting.table_number }}</strong> for your meeting with
            <strong>{{ theirLead.display_name }}</strong> from <strong>{{ theirLead.company.display_name }}</strong>.
          </p>

          <div class="inbox-row__attendees-title">
            <i class="far fa-people-group me-1"></i>
            {{ __('meeting.attendeesTitle') }}
            <span class="inbox-row__attendees-count">
              ({{ __('meeting.attendeesCount', { count: meeting.attendees.length }) }})
            </span>
          </div>
          <div class="inbox-row__attendees-container" style="justify-content: center">
            <div v-for="contact in meeting.attendees" :key="contact.id">
              <ContactAvatar :contact="contact" size="large" />
            </div>
          </div>

          <div style="margin-top: 2rem; background: var(--ui-grey-200); padding: var(--sizing-1); border-radius: var(--radius);">
            <CountdownTimer
              v-if="meeting?.start_datetime && meeting?.end_datetime"
              :deadline="countdownDeadline"
              :grace-period-seconds="0"
              format="span"
              size="medium"
              :countdown-intro-message="countdownMessage"
            ></CountdownTimer>
          </div>

        </div>

        <div v-else key="scanner">
          <QrScanner
            ref="scannerRef"
            @scanStarted="scannerActive = true"
            @scanStopped="scannerActive = false"
            @scanSuccess="handleScanSuccess"
            @scannerError="handleScannerError"
          />
          <p v-if="scanResult" class="scan-result">{{ scanResult }}</p>
        </div>
      </Transition>

      <template #footer>
        <div v-if="!checkinSuccess" class="button-group">
          <button
            v-if="!scannerActive"
            type="button"
            class="button"
            @click="startScan"
          >
            <i class="far fa-qrcode"></i> Start scanning
          </button>

          <button
            v-else
            type="button"
            class="button button--secondary"
            @click="stopScan"
          >
            <i class="far fa-times"></i> Cancel
          </button>
        </div>

        <div v-else class="button-group">
          <a class="button button--secondary" :href="`#/meetings/${meeting.id}/rate`">
            Rate meeting
          </a>
        </div>
      </template>
    </BaseModal>
  </div>
  <SoundPlayer ref="soundRef" />
</template>

<script setup>
import axios from 'axios';
import {
  ref, computed, onBeforeUnmount, watch,
} from 'vue';
import { store, user } from '~/Frontend/store';
import CountdownTimer from '~/Components/Frontend/CountdownTimer.vue';
import SoundPlayer from '~/Components/Frontend/SoundPlayer.vue';
import QrScanner from '~/Components/Frontend/QrScanner.vue';

const props = defineProps({
  checkInRoute: String,
});

const modal = ref(null);
const notifications = ref([]);
const scannerRef = ref(null);
const scannerActive = ref(false);
const scanResult = ref('');
const soundRef = ref(null);

const eventEdition = computed(() => store.eventEdition);
const activeContact = computed(() => user.activeContact);

const checkinSuccess = ref(false);
const meeting = ref(null);
const meetingStarted = ref(false);
const countdownMessage = ref('');
const countdownDeadline = ref(null);

const theirAttendees = computed(() => meeting.value?.attendees?.filter(
  (attendee) => attendee.company_id !== activeContact.value?.company_id,
) || []);

const theirLead = computed(() => theirAttendees.value.find((attendee) => attendee.is_lead));

const openModal = () => {
  notifications.value = [];
  scanResult.value = '';
  checkinSuccess.value = false;
  meeting.value = null;
  modal.value.open();
};

const startScan = () => {
  if (scannerActive.value) return;
  scannerRef.value?.startScanner?.();
};

const stopScan = () => {
  scannerRef.value?.stopScanner?.();
};

const cleanup = () => {
  stopScan();
};

const handleScanSuccess = async (decodedText) => {
  soundRef.value.play();

  const parts = decodedText.split('|');
  if (parts.length !== 3) {
    scanResult.value = 'Scanned non lounge QR code';
    return;
  }

  const [loungeId, loungeUuid, loungeName] = parts;
  scanResult.value = `Sending check-in request for ${loungeName}`;

  try {
    const url = props.checkInRoute.replace('%LOUNGEUUID%', loungeUuid);
    const response = await axios.post(url, {
      lounge_id: loungeId,
      lounge_uuid: loungeUuid,
      lounge_name: loungeName,
      contact_id: activeContact.value?.id,
      event_edition_id: eventEdition.value?.id,
    });

    const { data } = response;

    if (data.status === 'error') {
      notifications.value.push({
        type: 'error',
        message: data.message || 'Check-in failed.',
      });
      scanResult.value = '';
    } else if (data.status === 'success') {
      scanResult.value = '';
      checkinSuccess.value = true;
      meeting.value = data.meeting;

      const start = new Date(data.meeting.start_datetime);
      const end = new Date(data.meeting.end_datetime);
      const now = new Date();

      if (now < start) {
        countdownMessage.value = 'Meeting starts in';
        countdownDeadline.value = start.getTime();
        meetingStarted.value = false;
      } else {
        countdownMessage.value = 'Meeting ends in';
        countdownDeadline.value = end.getTime();
        meetingStarted.value = true;
      }

      if ('vibrate' in navigator) navigator.vibrate(100);
    } else {
      scanResult.value = '';
      notifications.value.push({
        type: 'error',
        message: 'Unexpected server response.',
      });
    }
  } catch (error) {
    scanResult.value = '';
    notifications.value.push({
      type: 'error',
      message: 'Check-in failed due to a network or server error.',
    });
  }
};

const handleScannerError = (error) => {
  notifications.value.push({
    type: 'error',
    message: error,
  });
};

let countdownTimeout = null;

const runCountdown = () => {
  const now = new Date();
  const start = new Date(meeting.value.start_datetime);
  const end = new Date(meeting.value.end_datetime);

  if (!meetingStarted.value && now >= start) {
    countdownMessage.value = 'Meeting ends in';
    countdownDeadline.value = end.getTime();
    meetingStarted.value = true;
  }

  countdownTimeout = setTimeout(runCountdown, 1000);
};

watch(countdownDeadline, () => {
  clearTimeout(countdownTimeout);
  runCountdown();
});

onBeforeUnmount(() => {
  clearTimeout(countdownTimeout);
});

</script>

<style scoped>
.checkin-success-instructions {
  margin-top: var(--sizing-4);
  font-size: var(--font-large);
  padding-bottom: var(--sizing-4);
  margin-bottom: var(--sizing-4);
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
.fade-scale-enter-to,
.fade-scale-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
